import { useState, useEffect, useCallback } from "react";
import AnimatedSection from "@/components/AnimatedSection";

const heroSlides = [
  {
    badge: "Laravel • PHP • SaaS • API • Cloud • AI",
    headline: "Laravel Experts Building Scalable Digital Products",
    highlightWords: "Laravel Experts",
    subtext:
      "We design and develop high-performance web applications, APIs, SaaS platforms and custom software using Laravel, PHP and modern technologies.",
    primaryCTA: "Start Your Project",
    primaryLink: "#contact",
    secondaryCTA: "View Our Work",
    secondaryLink: "#portfolio",
    trustLine: "Trusted by startups, businesses and growing enterprises",
    techLine: "Laravel • PHP • APIs • SaaS • Flutter • AWS",
    bgImage:
      "https://readdy.ai/api/search-image?query=Abstract%203D%20AI%20technology%20visual%20for%20a%20software%20company%20hero%20banner%2C%20flowing%20neural%20network%20mesh%20made%20of%20glowing%20particle%20dots%20and%20light%20filaments%20forming%20a%20soft%20organic%20wave%20shape%2C%20deep%20navy%20to%20black%20background%20with%20subtle%20radial%20glow%2C%20color%20palette%20of%20electric%20blue%2C%20violet%2C%20magenta%20and%20warm%20orange%20highlights%2C%20depth%20of%20field%20with%20floating%20bokeh%20particles%2C%20cinematic%20lighting%2C%20premium%20enterprise%20tech%20aesthetic%2C%20clean%20and%20uncluttered%20on%20the%20left%20side%2C%20no%20text%20no%20logos%20no%20human%20faces%2C%20wide%20landscape%2016:9%2C%20high%20detail&width=1600&height=900&seq=hero-ai-3d-2026&orientation=landscape",
  },
  {
    badge: "Enterprise Laravel Engineering",
    headline: "Build. Scale. Transform Your Business.",
    highlightWords: "Build. Scale.",
    subtext:
      "Modern software solutions engineered around your business goals with high performance and zero-downtime Cloud architecture.",
    primaryCTA: "Explore Our Services",
    primaryLink: "#services",
    secondaryCTA: "View Case Studies",
    secondaryLink: "#portfolio",
    trustLine: "High-performance Laravel applications built for growth",
    techLine: "Laravel • PHP • Redis • Queue • Microservices • Cloud",
    bgImage:
      "https://readdy.ai/api/search-image?query=Laravel%20backend%20architecture%20visualization%2C%20abstract%203D%20code%20structure%20diagram%20with%20blue%20and%20purple%20glow%2C%20API%20endpoints%20and%20database%20connections%2C%20modern%20tech%20aesthetic%2C%20floating%20geometric%20shapes%2C%20premium%20software%20engineering%20visual%2C%20no%20text&width=1400&height=800&seq=astrojal-slider-2&orientation=landscape",
  },
  {
    badge: "Full-Cycle Product Development",
    headline: "From Idea to Scalable Digital Product",
    highlightWords: "Scalable Digital Product",
    subtext:
      "Turn your complex business requirements into reliable, scalable digital web and mobile experiences on time and on budget.",
    primaryCTA: "Start Your Project",
    primaryLink: "#contact",
    secondaryCTA: "Hire Laravel Developers",
    secondaryLink: "/hire-laravel-developer",
    trustLine: "Delivering world-class web and mobile applications",
    techLine: "Vue.js • React • Flutter • Laravel • REST APIs • AWS",
    bgImage:
      "https://readdy.ai/api/search-image?query=Modern%20software%20product%20development%20UI%20mockup%2C%20mobile%20and%20web%20app%20screens%20floating%20in%203D%20space%2C%20blue%20and%20purple%20gradient%20background%2C%20clean%20minimal%20design%2C%20glassmorphism%20cards%2C%20premium%20digital%20product%20showcase%2C%20no%20text&width=1400&height=800&seq=astrojal-slider-3&orientation=landscape",
  },
  {
    badge: "Artificial Intelligence & Automation",
    headline: "AI-Powered Next Generation Web Solutions",
    highlightWords: "AI-Powered",
    subtext:
      "Bring modern LLMs, predictive intelligence, automated workflows, and custom AI chatbots into your web applications and SaaS products.",
    primaryCTA: "Explore AI Solutions",
    primaryLink: "/ai-solutions",
    secondaryCTA: "Book Consultation",
    secondaryLink: "/contact-us",
    trustLine: "Intelligent automation and smart software engineering",
    techLine: "AI • LLM • Machine Learning • Python • Laravel • Cloud",
    bgImage:
      "https://readdy.ai/api/search-image?query=AI%20artificial%20intelligence%20abstract%203D%20visualization%2C%20neural%20network%20nodes%20with%20blue%20and%20purple%20glow%2C%20modern%20cloud%20architecture%2C%20floating%20geometric%20shapes%2C%20data%20particles%2C%20premium%20tech%20aesthetic%2C%20gradient%20lighting%2C%20no%20text&width=1400&height=800&seq=astrojal-slider-4&orientation=landscape",
  },
];

function FloatingCard({
  label,
  icon,
  className,
  delay = 0,
}: {
  label: string;
  icon: string;
  className: string;
  delay?: number;
}) {
  return (
    <div
      className={`absolute flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-primary-900/80 border border-primary-700/50 backdrop-blur-sm text-sm md:text-[15px] font-semibold text-background-100 shadow-lg ${className}`}
      style={{
        animation: `float 5s ease-in-out ${delay}s infinite`,
      }}
    >
      <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-500/20 text-accent-400">
        <i className={`${icon} text-[13px]`} />
      </span>
      <span className="whitespace-nowrap">{label}</span>
    </div>
  );
}

function PulsingNode({ className, delay = 0 }: { className: string; delay?: number }) {
  return (
    <div className={`absolute ${className}`}>
      <div
        className="w-3 h-3 rounded-full bg-accent-500"
        style={{ animation: `pulse-dot 2.5s ease-in-out ${delay}s infinite` }}
      />
      <div
        className="absolute inset-0 w-3 h-3 rounded-full bg-accent-500/40"
        style={{ animation: `pulse-glow 2.5s ease-in-out ${delay}s infinite` }}
      />
    </div>
  );
}

function HeroVisual() {
  return (
    <div className="relative w-full h-full min-h-[320px] md:min-h-[420px] lg:min-h-[480px]">
      {/* Central Dashboard Card */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] md:w-[300px] lg:w-[340px] rounded-xl bg-primary-900/90 border border-primary-700/40 backdrop-blur-md p-4 shadow-2xl shadow-accent-500/5 animate-float-slow">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          </div>
          <span className="text-[10px] text-background-500 font-mono">dashboard.blade.php</span>
        </div>
        <div className="space-y-2">
          <div className="h-3 rounded bg-primary-700/50 w-full" />
          <div className="h-3 rounded bg-primary-700/50 w-[85%]" />
          <div className="h-3 rounded bg-primary-700/50 w-[60%]" />
          <div className="flex gap-2 mt-3">
            <div className="h-12 flex-1 rounded-lg bg-accent-500/15 border border-accent-500/20" />
            <div className="h-12 flex-1 rounded-lg bg-accent-500/15 border border-accent-500/20" />
            <div className="h-12 flex-1 rounded-lg bg-accent-500/15 border border-accent-500/20" />
          </div>
          <div className="flex gap-2 mt-1">
            <div className="h-16 flex-[2] rounded-lg bg-primary-700/30" />
            <div className="h-16 flex-1 rounded-lg bg-primary-700/30" />
          </div>
        </div>
      </div>

      {/* Floating Tech Cards */}
      <FloatingCard label="Laravel" icon="fa-brands fa-laravel" className="top-[8%] left-[5%] md:left-[8%]" delay={0} />
      <FloatingCard label="API" icon="ri-plug-line" className="top-[5%] right-[10%] md:right-[15%]" delay={0.5} />
      <FloatingCard label="Database" icon="ri-database-2-line" className="bottom-[15%] left-[3%] md:left-[5%]" delay={1} />
      <FloatingCard label="Cloud" icon="ri-cloud-line" className="bottom-[10%] right-[5%] md:right-[10%]" delay={1.5} />
      <FloatingCard label="Redis" icon="ri-server-line" className="top-[40%] right-[2%] md:right-[5%]" delay={0.8} />
      <FloatingCard label="Queue" icon="ri-list-check-2" className="top-[35%] left-[2%] md:left-[3%]" delay={1.2} />

      {/* Pulsing Nodes */}
      <PulsingNode className="top-[20%] left-[30%]" delay={0} />
      <PulsingNode className="top-[25%] right-[28%]" delay={0.7} />
      <PulsingNode className="bottom-[30%] left-[25%]" delay={1.4} />
      <PulsingNode className="bottom-[25%] right-[30%]" delay={2.1} />

      {/* Decorative gradient orbs */}
      <div className="absolute top-[10%] right-[20%] w-32 h-32 rounded-full bg-accent-500/10 blur-2xl animate-pulse-glow" />
      <div className="absolute bottom-[20%] left-[20%] w-24 h-24 rounded-full bg-secondary-500/10 blur-2xl animate-pulse-glow" style={{ animationDelay: "1.5s" }} />
    </div>
  );
}

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  // Continuous automatic slide change every 4 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const highlightText = (text: string, highlight: string) => {
    if (!highlight || !text.includes(highlight)) return text;
    const parts = text.split(highlight);
    return (
      <>
        {parts[0]}
        <span className="text-accent-400">{highlight}</span>
        {parts[1]}
      </>
    );
  };

  const slide = heroSlides[currentSlide];

  return (
    <section className="relative min-h-[720px] md:min-h-[840px] lg:min-h-[900px] flex items-center overflow-hidden bg-hero-gradient">
      {/* Background Images Crossfade */}
      <div className="absolute inset-0">
        {heroSlides.map((s, index) => (
          <img
            key={index}
            src={s.bgImage}
            alt={s.headline}
            title={s.badge}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100 z-0" : "opacity-0 -z-10"
            }`}
          />
        ))}
        {/* Readability overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/80 to-primary-950/40 pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/50 via-transparent to-primary-950 pointer-events-none" />
      </div>

      <div className="container-astro relative z-10 pt-28 md:pt-32 lg:pt-36 pb-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Text Content */}
          <div key={currentSlide} className="max-w-xl lg:max-w-none animate-fade-in-up" style={{ animationDuration: "0.5s" }}>
            <AnimatedSection delay={0}>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
                <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">
                  {slide.badge}
                </span>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[64px] font-extrabold text-background-50 leading-[1.08] mb-6 font-heading tracking-tight">
                {highlightText(slide.headline, slide.highlightWords)}
              </h1>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <p className="text-base md:text-lg text-background-400 leading-relaxed mb-8 max-w-lg">
                {slide.subtext}
              </p>
            </AnimatedSection>

            <AnimatedSection delay={300}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
                <a
                  href={slide.primaryLink}
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap btn-arrow-hover group"
                >
                  {slide.primaryCTA}
                  <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <a
                  href={slide.secondaryLink}
                  className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white text-sm font-semibold rounded-xl hover:bg-white/10 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover group"
                >
                  {slide.secondaryCTA}
                  <i className="ri-external-link-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={400}>
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-7 h-7 rounded-full bg-primary-700 border-2 border-primary-950 flex items-center justify-center text-[9px] text-background-400 font-bold"
                    >
                      <i className="ri-user-line" />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-background-500">
                  {slide.trustLine}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={500}>
              <div className="flex items-center gap-3 mt-10 text-xs md:text-sm text-background-600 font-medium tracking-wider flex-wrap">
                {slide.techLine.split(" \u2022 ").map((tech, i) => (
                  <span key={tech} className="flex items-center gap-3">
                    <span className="text-background-500">{tech}</span>
                    {i < slide.techLine.split(" \u2022 ").length - 1 && (
                      <span className="w-1 h-1 rounded-full bg-background-700" />
                    )}
                  </span>
                ))}
              </div>
            </AnimatedSection>
          </div>

          {/* Hero Visual */}
          <div className={`relative transition-all duration-1000 ${isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <HeroVisual />
          </div>
        </div>

        {/* Hero Carousel Navigation Controls & Dots Bar */}
        <div className="mt-12 flex items-center justify-between border-t border-white/10 pt-6">
          {/* Indicator Dots */}
          <div className="flex items-center gap-2">
            {heroSlides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  idx === currentSlide
                    ? "w-8 h-2.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316]"
                    : "w-2.5 h-2.5 bg-white/20 hover:bg-white/40"
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
            <span className="text-xs text-background-400 ml-2 font-mono">
              0{currentSlide + 1} / 0{heroSlides.length}
            </span>
          </div>

          {/* Navigation Prev & Next Arrows */}
          <div className="flex items-center gap-3">
            <button
              onClick={prevSlide}
              className="w-10 h-10 rounded-xl bg-primary-900/80 border border-white/20 text-white flex items-center justify-center hover:bg-white/10 hover:border-white/40 transition-all cursor-pointer"
              aria-label="Previous Slide"
              title="Previous Slide"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              onClick={nextSlide}
              className="w-10 h-10 rounded-xl bg-primary-900/80 border border-white/20 text-white flex items-center justify-center hover:bg-white/10 hover:border-white/40 transition-all cursor-pointer"
              aria-label="Next Slide"
              title="Next Slide"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary-950 to-transparent pointer-events-none" />
    </section>
  );
}